import { getRpcUrlByNetworkId } from '@optimex-xyz/trade-wallet-sdk'
import { Wallet, WalletContextState } from '@solana/wallet-adapter-react'
import { Connection, PublicKey, Transaction } from '@solana/web3.js'

import { INetwork } from '@/types'
import { WALLET_SOLANA } from '@/utils'

import { BaseWallet } from './BaseWallet'
import { IWalletConnectOptions, PsbtSignOptions, TransactionOptions } from './types'

export class SolanaWallet extends BaseWallet {
  name = 'SolanaWallet'
  walletId = '' // can be solflare, phantom, ... adapter.name
  walletName = '' // adapter.name
  key = WALLET_SOLANA
  address = ''
  onAccountChanged: IWalletConnectOptions['onAccountChanged']
  onDisconnected: IWalletConnectOptions['onDisconnected']

  public get walletContext(): WalletContextState {
    return window.solanaContext
  }

  async getAddress() {
    return this.address ?? ''
  }

  isInstalled(): boolean {
    return true
  }

  private handleConnectSuccess({
    publicKey,
    walletName,
    options,
  }: {
    publicKey: PublicKey
    walletName: string
    options: IWalletConnectOptions
  }) {
    this.address = publicKey?.toBase58() ?? ''
    this.walletId = walletName ?? ''
    this.walletName = walletName ?? ''
    return {
      address: publicKey.toBase58(),
      networkInfo: options.network,
      compressedPublicKey: publicKey.toBase58(),
    }
  }

  async connect(
    _network: string,
    options: IWalletConnectOptions
  ): Promise<{ address: string; networkInfo: INetwork; compressedPublicKey: string | undefined }> {
    const { onAccountChanged, onDisconnected } = options
    const wallet = options?.adapter as Wallet
    const { connect, select } = this.walletContext

    if (!wallet || !connect) throw new Error('Connector is not initialized')

    this.onAccountChanged = onAccountChanged
    this.onDisconnected = onDisconnected

    const { adapter } = wallet
    const publicKey: PublicKey = await new Promise((resolve, reject) => {
      try {
        select(adapter?.name)
        if (adapter?.connected && adapter?.publicKey) {
          return resolve(adapter?.publicKey)
        }
        wallet?.adapter?.once('connect', resolve)
      } catch (error) {
        console.error('solana wallet connect error', error)
        reject(error)
      }
    })
    return this.handleConnectSuccess({ publicKey, walletName: adapter?.name, options })
  }

  async disconnect(): Promise<void> {
    const { disconnect } = this.walletContext
    disconnect?.()
    this.address = ''
  }

  async sendTransaction(
    _toAddress: string,
    _amount: number | bigint,
    network: string,
    options?: TransactionOptions
  ): Promise<string> {
    const { signTransaction, publicKey } = this.walletContext
    if (!signTransaction || !publicKey) throw new Error('Solana wallet not connected')

    const connection = this.getConnection(network)

    if (options?.data) {
      const transaction = Transaction.from(Buffer.from(options.data, 'hex'))
      const signedTx = await signTransaction(transaction)

      const signature = await connection.sendRawTransaction(signedTx.serialize(), { preflightCommitment: 'confirmed' })

      return signature
    }

    throw new Error('Amount must be greater than 0')
  }

  async getWalletAddress() {
    return this.address
  }

  private getConnection(network: string) {
    return new Connection(getRpcUrlByNetworkId(network), 'confirmed')
  }

  async signMessage(message: string): Promise<string> {
    const { signMessage } = this.walletContext
    if (!signMessage) throw new Error('Wallet is not connected')
    const encodedMessage = new TextEncoder().encode(message)
    const signature = await signMessage(encodedMessage)
    return Buffer.from(signature).toString('base64')
  }

  async getCompressedPublicKey(): Promise<string> {
    return this.address ?? ''
  }

  async signPsbt(_psbt: string, _network: string, _options?: PsbtSignOptions): Promise<string> {
    throw new Error('SignPsbt is not supported for Solana Wallet')
  }

  priority(): number {
    return 2
  }
}
